import { EventType } from "@/analytics/analytics-events";
import {
  ChangeRegistrationCameraMode,
  ChangeRegistrationCameraPerspectiveProperties,
  ChangeRegistrationCameraProjectionProperties,
} from "@/analytics/staging-area-workflows-events-properties";
import { SmartNavigationCircle } from "@/components/r3f/controls/smart-navigation/smart-navigation-circle";
import { CameraSwitch } from "@/components/ui/camera-switch";
import { ClickThroughStack } from "@/components/ui/click-through-stack";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { ToolName } from "@/store/ui/ui-slice";
import { Analytics } from "@faro-lotv/foreign-observers";
import { RegistrationRevision } from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { memo, useCallback, useEffect, type JSX } from "react";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { RenderingDebugOverlay } from "../rendering/debug/rendering-debug-overlay";
import {
  ScanColoring,
  setScanColoring,
} from "../store/data-preparation-view-options/data-preparation-view-options-slice";
import { selectIsAnyEditModeEnabled } from "../store/user-edits/data-preparation-user-edit-selectors";
import { DataPreparationActions } from "./data-preparation-actions";
import { DataPreparationBanners } from "./data-preparation-banners";
import { DataPreparationHelpBanner } from "./data-preparation-help-banner";
import { EditModeToolbar } from "./edit-mode-toolbar";
import { PerspectiveSwitch } from "./perspective-switch";
import { Projection, ProjectionSwitch } from "./projection-switch";
import { useRegistrationReportContext } from "./report-context";
import { RevisionContextualToolbar } from "./revision-contextual-toolbar";
import { DataPreparationViewSettingsToolbar } from "./view-settings-toolbar/data-preparation-view-settings-toolbar";

/**
 * @param topRowProps Props to forward to the top row of the overlay.
 * @returns the canvas overlay for the data preparation overlay.
 */
export const DataPreparationCanvasOverlay = memo(
  function DataPreparationCanvasOverlay(
    topRowProps: OverlayTopRowProps,
  ): JSX.Element {
    useAutoSetScanColoring();
    return (
      <ClickThroughStack
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        {!topRowProps.isEditRegistrationEnabled && (
          <DataPreparationBanners revision={topRowProps.revision} />
        )}

        <ClickThroughStack
          sx={{
            gap: 2,
            width: "100%",
            p: 2,
            justifyContent: "space-between",
            alignItems: "center",
            flexGrow: 1,
          }}
        >
          <OverlayTopRow {...topRowProps} />

          <OverlayBottomSection />

          {/* Rendering overlay is dev-only and lives outside the relative layout */}
          <RenderingDebugOverlay />
        </ClickThroughStack>

        <SmartNavigationCircle />
      </ClickThroughStack>
    );
  },
);

type OverlayTopRowProps = {
  revision: RegistrationRevision;
  isEditRegistrationEnabled: boolean;
};

/** @returns the topmost row of the overlay, containing the action buttons and help banner. */
function OverlayTopRow({ revision }: OverlayTopRowProps): JSX.Element {
  const { setIsReportOpen } = useRegistrationReportContext();
  return (
    <ClickThroughStack
      direction="row"
      sx={{
        gap: 2,
        justifyContent: "space-between",
        alignItems: "start",
        width: "100%",
      }}
    >
      <Stack
        component="div"
        sx={{ flexGrow: 1, justifyContent: "center", alignItems: "center" }}
      >
        <DataPreparationHelpBanner />
      </Stack>

      <DataPreparationActions
        revision={revision}
        onToggleReport={setIsReportOpen}
      />
    </ClickThroughStack>
  );
}

/** @returns the bottom section of the canvas overlay, containing the different toolbars. */
function OverlayBottomSection(): JSX.Element {
  return (
    <ClickThroughStack
      data-testid="bottom-part"
      sx={{
        justifyContent: "space-between",
        alignItems: "end",
        flexDirection: "row",
        width: "100%",
        flexGrow: 1,
        gap: 2,
      }}
    >
      {/* Left section */}
      <OverlayCameraTools />

      {/* Middle section */}
      <RevisionContextualToolbar />

      {/* Right section */}
      <OverlayToolbars />
    </ClickThroughStack>
  );
}

/** @returns tools related to camera configuration. */
function OverlayCameraTools(): JSX.Element {
  const {
    projection,
    setProjection,
    perspective,
    setPerspective,
    cameraOptions3D,
    setCameraOptions3D,
    centerCameraEvent,
  } = useDataPreparationOverlayElements();

  return (
    <ClickThroughStack
      sx={{
        justifyContent: "end",
        alignItems: "start",
        height: "100%",
        gap: 2,
      }}
    >
      {projection === Projection.twoDimensional && (
        <PerspectiveSwitch
          perspective={perspective}
          onPerspectiveChange={(value) => {
            Analytics.track<ChangeRegistrationCameraPerspectiveProperties>(
              EventType.changeRegistrationCameraPerspective,
              {
                perspective: value,
              },
            );

            setPerspective(value);
            centerCameraEvent.emit({ perspective: value });
          }}
        />
      )}

      {projection === Projection.threeDimensional && (
        <CameraSwitch
          shouldShowSmartNavigationToggle
          cameraOptions3D={cameraOptions3D}
          onCameraChange={(value) => {
            Analytics.track<ChangeRegistrationCameraProjectionProperties>(
              EventType.changeRegistrationCameraProjection,
              {
                projection: value,
              },
            );

            setCameraOptions3D(value);
          }}
        />
      )}

      <ProjectionSwitch
        projection={projection}
        onProjectionChange={(newProjection) => {
          Analytics.track<ChangeRegistrationCameraMode>(
            EventType.changeRegistrationCameraMode,
            {
              variant: newProjection,
            },
          );

          setProjection(newProjection);
        }}
      />
    </ClickThroughStack>
  );
}

/** @returns toolbars for view settings and edit mode. */
function OverlayToolbars(): JSX.Element {
  const { activeTool, setActiveTool, perspective, centerCameraEvent } =
    useDataPreparationOverlayElements();

  const toggleTool = useCallback(
    (tool: ToolName) => {
      if (activeTool === tool) {
        setActiveTool(undefined);
      } else {
        setActiveTool(tool);
      }
    },
    [activeTool, setActiveTool],
  );

  return (
    <ClickThroughStack
      sx={{ height: "100%", justifyContent: "flex-start", minHeight: 0 }}
    >
      <DataPreparationViewSettingsToolbar
        onFocusOnProject={() => centerCameraEvent.emit({ perspective })}
        activeTool={activeTool}
        toggleTool={toggleTool}
      />
      {/* Flexible spacer that absorbs available space between the two toolbars
          without pushing EditModeToolbar off-screen on smaller viewports */}
      <div style={{ flexGrow: 1, minHeight: 8, maxHeight: 160 }} />
      <EditModeToolbar />
    </ClickThroughStack>
  );
}

/**
 * Automatically set the scan coloring option based on the edit mode.
 */
function useAutoSetScanColoring(): void {
  const dispatch = useAppDispatch();
  const { projection } = useDataPreparationOverlayElements();

  const isEditModeEnabled = useAppSelector(selectIsAnyEditModeEnabled);

  useEffect(() => {
    if (isEditModeEnabled) {
      dispatch(setScanColoring(ScanColoring.byScan));
    } else if (projection === Projection.twoDimensional) {
      dispatch(setScanColoring(ScanColoring.intensity));
    } else {
      dispatch(setScanColoring(ScanColoring.originalColor));
    }
  }, [dispatch, projection, isEditModeEnabled]);
}
